import { get, post } from './api'

const BOOKING_PREFIX = '/blade-portal/booking'
const FREIGHT_PREFIX = '/blade-portal/freight'
const SENSITIVE_GOODS_PREFIX = '/blade-portal/sensitive-goods'
const BASE_DATA_PREFIX = '/blade-portal/base-data'

export const bookingService = {
  /**
   * 获取运费报价 - 比较预估 (访客可用)
   */
  async getFreightEstimate(data) {
    const response = await post(`${FREIGHT_PREFIX}/estimate`, {
      cargoName: data.cargoName,
      weight: data.weight,
      volume: data.volume,
      destinationCountry: data.destinationCountry,
      shippingMethod: data.shippingMethod,
    })
    return response
  },

  /**
   * 获取精准报价 - 精准预估 (访客可用)
   */
  async getPreciseEstimate(data) {
    const response = await post(`${FREIGHT_PREFIX}/precise-estimate`, {
      cargoName: data.cargoName,
      weight: data.weight,
      volume: data.volume,
      length: data.length,
      width: data.width,
      height: data.height,
      destinationCountry: data.destinationCountry,
      destinationCity: data.destinationCity,
      detailAddress: data.detailAddress,
      shippingMethod: data.shippingMethod,
    })
    return response
  },

  /**
   * 获取可用渠道 (访客可用)
   */
  async getAvailableChannels(params = {}) {
    const response = await get(`${FREIGHT_PREFIX}/channels`, {
      destinationCountry: params.destinationCountry,
      shippingMethod: params.shippingMethod,
    })
    return response
  },

  /**
   * 创建集运报名（支持游客）
   */
  async createBooking(data) {
    const response = await post(`${BOOKING_PREFIX}/create`, {
      contactName: data.contactName,
      contactPhone: data.contactPhone,
      cargoName: data.cargoName,
      itemType: data.itemType,
      itemName: data.itemName,
      cargoDescription: data.cargoDescription,
      weight: data.weight,
      volume: data.volume,
      length: data.length,
      width: data.width,
      height: data.height,
      quantity: data.quantity,
      declaredValue: data.declaredValue,
      pickupRequired: data.pickupRequired,
      pickupAddress: data.pickupAddress,
      pickupCountry: data.pickupCountry,
      pickupProvince: data.pickupProvince,
      pickupCity: data.pickupCity,
      pickupDistrict: data.pickupDistrict,
      pickupStreet: data.pickupStreet,
      pickupDetailAddress: data.pickupDetailAddress,
      destinationCountry: data.destinationCountry,
      destinationCity: data.destinationCity,
      deliveryAddress: data.deliveryAddress,
      needDelivery: data.needDelivery,
      shippingMethod: data.shippingMethod,
      logisticsOption: data.logisticsOption,
      serviceProvider: data.serviceProvider,
      channelId: data.channelId,
      remarks: data.remarks,
    })
    return response
  },

  /**
   * 获取客户的报名列表
   */
  async getBookings(params = {}) {
    const response = await get(`${BOOKING_PREFIX}/list`, {
      current: params.current || 1,
      size: params.size || 20,
      status: params.status,
    })
    return response
  },

  /**
   * 获取单个报名详情
   */
  async getBookingDetail(id) {
    const response = await get(`${BOOKING_PREFIX}/detail`, { id })
    return response
  },
  
  /*
   * 【已移除 · 2026-07-31】getBookingByCode -> GET /detail-by-code
   *
   * 唯一调用方是 CargoDetailPage，该页已删除（报名详情并入 CargoPage 页内视图）。
   * 不要再接回来：这个后端接口**免鉴权、不校验手机后四位**，凭一个顺序单号
   * （CB-2026-000xx，可枚举）就能取到含联系人与手机号的完整记录 —— 前端一旦调用它，
   * 就等于把「我的报名」「集运报名」两处刚做的身份校验全部绕过。
   * 需要按单号取数请用下面的 verifyBookingByCode。后端接口本身未动。
   */

  /**
   * 报名编号 + 手机号后四位校验查询（游客查询、或登录用户查询非本人订单时使用）
   */
  async verifyBookingByCode(bookingCode, phoneTail) {
    const response = await get(`${BOOKING_PREFIX}/verify-by-code`, { bookingCode, phoneTail })
    return response
  },

  /**
   * 取消报名
   */
  async cancelBooking(id) {
    const response = await post(`${BOOKING_PREFIX}/cancel`, { id })
    return response
  },

  /**
   * 客户拒绝报名
   */
  async rejectBooking(id) {
    const response = await post(`${BOOKING_PREFIX}/reject`, { id })
    return response
  },

  /**
   * 客户接受报价
   */
  async acceptBooking(id) {
    const response = await post(`${BOOKING_PREFIX}/accept`, { id })
    return response
  },

  /*
   * 以下两个 by-code 版本供「游客提交的报名」自助操作。
   * 这类单 customer_id 为空，上面按 id 的两个接口按 customerId 过滤、永远匹配不到它，
   * 游客也没 token 过不了后端的 requireLogin，所以只能凭「单号 + 报名手机号后四位」授权。
   * 后端会拒绝有主的单（账号提交的必须登录该账号操作），并对后四位做失败计数锁定。
   * 后四位由页面在校验成功时留在内存里，用户不需要再输一遍。
   */

  /**
   * 凭单号 + 手机后四位拒绝报名（仅游客提交的单）
   */
  async rejectBookingByCode(bookingCode, phoneTail) {
    const response = await post(`${BOOKING_PREFIX}/reject-by-code?bookingCode=${encodeURIComponent(bookingCode)}&phoneTail=${encodeURIComponent(phoneTail)}`)
    return response
  },

  /**
   * 凭单号 + 手机后四位接受报价（仅游客提交的单）
   */
  async acceptBookingByCode(bookingCode, phoneTail) {
    const response = await post(`${BOOKING_PREFIX}/accept-by-code?bookingCode=${encodeURIComponent(bookingCode)}&phoneTail=${encodeURIComponent(phoneTail)}`)
    return response
  },

  /**
   * 获取敏感货配置选项
   */
  async getSensitiveGoodsOptions(destinationCountry) {
    const response = await get(`${SENSITIVE_GOODS_PREFIX}/options`, {
      destinationCountry,
    })
    return response
  },

  /**
   * 检查是否敏感货
   */
  async checkSensitiveGoods(params) {
    const response = await get(`${SENSITIVE_GOODS_PREFIX}/check`, {
      destinationCountry: params.destinationCountry,
      itemType: params.itemType,
      itemName: params.itemName,
    })
    return response
  },

  /**
   * 收货国家列表
   */
  async getDestinationCountries() {
    const response = await get(`${BASE_DATA_PREFIX}/countries`)
    return response
  },

  /**
   * 行政区划子节点
   */
  async getRegionChildren(parentCode = '00') {
    const response = await get(`${BASE_DATA_PREFIX}/regions`, { parentCode })
    return response
  },

  /**
   * 根据国家查询仓库地址列表
   */
  async getWarehouseAddresses(country) {
    const response = await get(`${FREIGHT_PREFIX}/warehouse-addresses`, { country })
    return response
  },

  /**
   * 比较预估运费估算（客户端）
   */
  async getCompareEstimate(params) {
    const response = await get(`${FREIGHT_PREFIX}/compare-estimate`, {
      country: params.country,
      hasElectromagnetic: params.hasElectromagnetic,
      weight: params.weight,
      shippingMethod: params.shippingMethod || '',
    })
    return response
  },

  /**
   * 精准预估运费报价（客户端）
   */
  async getPreciseQuote(params) {
    const response = await get(`${FREIGHT_PREFIX}/precise-quote`, {
      destinationCountry: params.destinationCountry,
      itemType: params.itemType,
      itemName: params.itemName,
      weight: params.weight,
      volume: params.volume || null,
      shippingMethod: params.shippingMethod || '',
    })
    return response
  },
}

export default bookingService
